package com.bulut.mecitogullari.ui.screens.newproject

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.nowEpochMillis
import com.bulut.mecitogullari.data.model.Project
import com.bulut.mecitogullari.data.model.ProjectBlock
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.UnitRepository
import com.bulut.mecitogullari.ui.theme.AppColors
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Editable block row in the new-project form. */
data class BlockInput(
    val name: String = "",
    val floorsText: String = "",
    val apartmentsPerFloorText: String = "",
)

data class NewProjectUiState(
    val name: String = "",
    val location: String = "",
    val startDateEpochMillis: Long? = null,
    val expectedEndEpochMillis: Long? = null,
    /** One row per block; a single row stays unnamed. */
    val blocks: List<BlockInput> = listOf(BlockInput()),
    val notes: String = "",
    val coverColorHex: String = AppColors.CoverOptions.first(),
    val error: String? = null,
    val isSaving: Boolean = false,
    val saved: Boolean = false,
)

class NewProjectViewModel(
    private val projectRepository: ProjectRepository,
    private val unitRepository: UnitRepository,
) : ViewModel() {

    private val _uiState = MutableStateFlow(NewProjectUiState())
    val uiState: StateFlow<NewProjectUiState> = _uiState.asStateFlow()

    fun onNameChange(v: String) = _uiState.update { it.copy(name = v, error = null) }
    fun onLocationChange(v: String) = _uiState.update { it.copy(location = v) }
    fun onStartDateChange(millis: Long) = _uiState.update { it.copy(startDateEpochMillis = millis) }
    fun onEndDateChange(millis: Long) = _uiState.update { it.copy(expectedEndEpochMillis = millis) }
    fun onBlockNameChange(index: Int, v: String) = updateBlock(index) { it.copy(name = v) }

    fun onFloorsChange(index: Int, v: String) =
        updateBlock(index) { it.copy(floorsText = v.filter(Char::isDigit)) }

    fun onApartmentsPerFloorChange(index: Int, v: String) =
        updateBlock(index) { it.copy(apartmentsPerFloorText = v.filter(Char::isDigit)) }

    /** New rows get the next free letter; the first row stays unnamed until
     *  a second block exists, so single-block projects keep clean labels. */
    fun onAddBlock() = _uiState.update { state ->
        val blocks = state.blocks.mapIndexed { index, block ->
            if (index == 0 && block.name.isBlank()) block.copy(name = blockLetter(0)) else block
        } + BlockInput(name = blockLetter(state.blocks.size))
        state.copy(blocks = blocks, error = null)
    }

    fun onRemoveBlock(index: Int) = _uiState.update { state ->
        if (state.blocks.size <= 1) return@update state
        val blocks = state.blocks.filterIndexed { i, _ -> i != index }
        state.copy(
            blocks = if (blocks.size == 1) listOf(blocks.first().copy(name = "")) else blocks,
            error = null,
        )
    }

    private fun updateBlock(index: Int, transform: (BlockInput) -> BlockInput) =
        _uiState.update { state ->
            state.copy(
                blocks = state.blocks.mapIndexed { i, block ->
                    if (i == index) transform(block) else block
                },
                error = null,
            )
        }
    fun onNotesChange(v: String) = _uiState.update { it.copy(notes = v) }
    fun onCoverColorChange(hex: String) = _uiState.update { it.copy(coverColorHex = hex) }

    fun save() {
        val state = _uiState.value
        if (state.isSaving) return

        if (state.name.isBlank()) {
            return _uiState.update { it.copy(error = Localization.strings.errorProjectName) }
        }
        val multiBlock = state.blocks.size > 1
        val blocks = state.blocks.mapIndexed { index, input ->
            ProjectBlock(
                name = if (multiBlock) input.name.trim().ifBlank { blockLetter(index) } else "",
                floors = input.floorsText.toIntOrNull() ?: 0,
                apartmentsPerFloor = input.apartmentsPerFloorText.toIntOrNull() ?: 0,
            )
        }
        val layoutOk = blocks.all { it.floors in 1..60 && it.apartmentsPerFloor in 1..12 }
        if (!layoutOk) {
            return _uiState.update { it.copy(error = Localization.strings.errorBlockLayout) }
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true, error = null) }
            val project = Project(
                id = newId(),
                name = state.name.trim(),
                location = state.location.trim(),
                startDateEpochMillis = state.startDateEpochMillis,
                expectedEndEpochMillis = state.expectedEndEpochMillis,
                // Legacy fields mirror the first block so older readers still work.
                floors = blocks.first().floors,
                apartmentsPerFloor = blocks.first().apartmentsPerFloor,
                blocks = blocks,
                notes = state.notes.trim(),
                coverColorHex = state.coverColorHex,
                createdAtEpochMillis = nowEpochMillis(),
            )
            projectRepository.createProject(project)
            unitRepository.generateForProject(project)
            _uiState.update { it.copy(isSaving = false, saved = true) }
        }
    }

    /** A, B, C… (AA, AB… past the 26th block). */
    private fun blockLetter(index: Int): String =
        if (index < 26) {
            ('A' + index).toString()
        } else {
            "${'A' + (index / 26 - 1)}${'A' + (index % 26)}"
        }

    /** Reset the one-shot saved flag after navigation has been handled. */
    fun onSavedHandled() = _uiState.update { it.copy(saved = false) }
}
